import { observer } from "mobx-react";
import { IAppliedFilterChipComponent } from "../BaseChipComponents/IAppliedFilterChipComponent";
import AppliedFilterChip from "../BaseChipComponents/AppliedFilterChipBase";
import { useStore } from "utils/useStore";
import React, { useCallback } from "react";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import { Grid } from "@mui/material";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import Classifier from "models/ClassifierModel";
import { buildHoverList, CHIP_AGGREGATION_THRESHOLD, chipAggregatedLabel, chipItemLabel, chipModelName, sortAlphabetically, useChipFiltersStore } from "../BaseChipComponents/chipUtils";

export const ClassifierChips = observer(function (
    props: IAppliedFilterChipComponent,
) {
    const applicationFiltersStore = useChipFiltersStore(props);
    const applicationFilterValuesStore = useStore(ApplicationFilterValuesStore);
    const messageStore = useStore(MessageStore);

    const removeClassifier = useCallback(
        (classifier?: Classifier) => {
            let success;
            if (!classifier) success = false;
            else
                success = applicationFiltersStore.removeClassifierById(
                    classifier.id,
                );

            if (!success)
                messageStore.logError(
                    "Error removing classifier. Classifier not found.",
                );
        },
        [messageStore, applicationFiltersStore],
    );

    const rbcFilterItems = applicationFiltersStore.rbcFilterItems;
    if (rbcFilterItems.flatMap((item) => item.classifierIds).length === 0)
        return null;

    const isClassifiersLoading = applicationFilterValuesStore.getTaskLoading(
        ApplicationFilterValuesStore.Tasks.LOAD_USER_CLASSIFIERS,
    );

    // Group classifier IDs by their operator (contains vs. does-not-contain)
    const containsIds: string[] = [];
    const notContainsIds: string[] = [];
    for (const item of rbcFilterItems) {
        if (item.value) {
            containsIds.push(...item.classifierIds);
        } else {
            notContainsIds.push(...item.classifierIds);
        }
    }

    if (isClassifiersLoading) {
        const loadingChip = (key: string) => (
            <AppliedFilterChip
                key={key}
                disabled={props.disabled}
                isApplicable={props.isApplicable}
            >
                <Grid
                    container
                    direction="row"
                    flexWrap="nowrap"
                    alignItems="center"
                    spacing={1}
                >
                    <Grid item>Classifier:</Grid>
                    <Grid item>
                        <AcxLoadingIndicator size={16} alternate="PuffLoader" />
                    </Grid>
                </Grid>
            </AppliedFilterChip>
        );
        const containsChips =
            containsIds.length > CHIP_AGGREGATION_THRESHOLD
                ? loadingChip("loading-contains-group")
                : containsIds.map((id) => loadingChip(id));
        const notContainsChips =
            notContainsIds.length > CHIP_AGGREGATION_THRESHOLD
                ? loadingChip("loading-not-contains-group")
                : notContainsIds.map((id) => loadingChip(id));
        return (
            <>
                {containsChips}
                {notContainsChips}
            </>
        );
    }

    const resolve = (id: string) =>
        applicationFilterValuesStore.userClassifiers?.find(
            (c) => c.id === id,
        );

    const renderGroup = (
        ids: string[],
        isExclusion: boolean,
    ): React.ReactNode => {
        if (ids.length === 0) return null;

        if (ids.length > CHIP_AGGREGATION_THRESHOLD) {
            const sortedNames = sortAlphabetically(
                ids.map((id) => chipModelName(resolve(id)?.name ?? id)),
            );

            return (
                <AppliedFilterChip
                    key={isExclusion ? "exclude-group" : "include-group"}
                    disabled={props.disabled}
                    isApplicable={props.isApplicable}
                    onRemove={() => {
                        ids.forEach((id) => {
                            const c = resolve(id);
                            if (c)
                                applicationFiltersStore.removeClassifierById(
                                    c.id,
                                );
                        });
                    }}
                    tooltipContent={buildHoverList(sortedNames)}
                >
                    {chipAggregatedLabel(ids.length, "Classifiers", isExclusion)}
                </AppliedFilterChip>
            );
        }

        return ids.map((id, index) => {
            const classifier = resolve(id);
            return (
                <AppliedFilterChip
                    key={classifier?.id ?? index}
                    disabled={props.disabled}
                    isApplicable={props.isApplicable}
                    onRemove={
                        !classifier
                            ? undefined
                            : () => removeClassifier(classifier)
                    }
                >
                    {chipItemLabel("Classifier", chipModelName(classifier?.name ?? ""), isExclusion)}
                </AppliedFilterChip>
            );
        });
    };

    return (
        <>
            {renderGroup(containsIds, false)}
            {renderGroup(notContainsIds, true)}
        </>
    );
});
